import React, { useCallback, useMemo, useEffect, useState } from 'react';
import ReactFlow, {
  Background,
  Controls,
  Handle,
  Position,
  useNodesState,
  useEdgesState,
  useReactFlow,
  ReactFlowProvider,
  MarkerType,
} from 'reactflow';
import dagre from 'dagre';
import 'reactflow/dist/style.css';

// ── Node dimensions ──
const NODE_W = 220;
const NODE_H = 60;

// ── Category colors ──
const CATEGORY_COLORS = {
  cathode:              { bg: '#FFE8E5', border: '#FB8072', text: '#c0392b' },
  anode:                { bg: '#E6F2F9', border: '#80B1D3', text: '#2471a3' },
  separator:            { bg: '#FFF1E0', border: '#FDB462', text: '#b8860b' },
  electrolyte:          { bg: '#F0EFF9', border: '#BEBADA', text: '#6c3483' },
  assembly:             { bg: '#E6F4EA', border: '#81C784', text: '#1b5e20' },
  preparation:          { bg: '#E6F4EA', border: '#81C784', text: '#1b5e20' },
  disassembly:          { bg: '#FFF8E1', border: '#FFD54F', text: '#f57f17' },
  chemical_processing:  { bg: '#E0F7FA', border: '#4DD0E1', text: '#00838f' },
  thermal_processing:   { bg: '#FCE4EC', border: '#EF9A9A', text: '#c62828' },
  recycling:            { bg: '#E8F5E9', border: '#66BB6A', text: '#2e7d32' },
  coating:              { bg: '#F3E5F5', border: '#BA68C8', text: '#6a1b9a' },
  electrode:            { bg: '#FFF3E0', border: '#FFB74D', text: '#e65100' },
  default:              { bg: '#F5F5F5', border: '#BDBDBD', text: '#424242' },
};

const getColor = (category) => {
  const key = (category || 'default').toLowerCase();
  return CATEGORY_COLORS[key] || CATEGORY_COLORS.default;
};

// ── Custom process node ──
const ProcessNode = ({ data }) => {
  const color = getColor(data.category);
  const isSelected = data.isSelected;

  return (
    <div
      style={{
        width: NODE_W,
        height: NODE_H,
        background: color.bg,
        border: `2px solid ${isSelected ? '#1a73e8' : color.border}`,
        borderRadius: 8,
        display: 'flex',
        flexDirection: 'column',
        alignItems: 'center',
        justifyContent: 'center',
        cursor: 'pointer',
        boxShadow: isSelected
          ? '0 0 0 2px #1a73e8, 0 2px 8px rgba(26,115,232,0.25)'
          : '0 1px 4px rgba(0,0,0,0.1)',
        transition: 'box-shadow 0.15s, border-color 0.15s',
        padding: '4px 8px',
        overflow: 'hidden',
        position: 'relative',
      }}
    >
      <Handle type="target" position={Position.Top} style={{ opacity: 0, width: 8, height: 8 }} />
      <Handle type="target" position={Position.Left} id="left-target" style={{ opacity: 0, width: 8, height: 8 }} />
      <div style={{
        fontSize: 14,
        fontWeight: 600,
        color: color.text,
        textAlign: 'center',
        lineHeight: 1.2,
        maxWidth: '100%',
        overflow: 'hidden',
        textOverflow: 'ellipsis',
        whiteSpace: 'nowrap',
      }}>
        {data.label}
      </div>
      <div style={{
        fontSize: 11,
        color: '#888',
        marginTop: 2,
        fontWeight: 500,
      }}>
        {data.processId}
        {data.categoryLabel ? ` · ${data.categoryLabel}` : ''}
      </div>
      <Handle type="source" position={Position.Bottom} style={{ opacity: 0, width: 8, height: 8 }} />
      <Handle type="source" position={Position.Right} id="right-source" style={{ opacity: 0, width: 8, height: 8 }} />
    </div>
  );
};

// ── Collapsed group node — clean pill style ──
const GroupNode = ({ data }) => {
  const color = getColor(data.category);

  return (
    <div
      style={{
        width: NODE_W,
        height: NODE_H,
        background: color.bg,
        border: `2px solid ${color.border}`,
        borderRadius: 8,
        display: 'flex',
        alignItems: 'center',
        justifyContent: 'center',
        cursor: 'pointer',
        boxShadow: '0 1px 4px rgba(0,0,0,0.1)',
        padding: '4px 12px',
        position: 'relative',
        gap: 8,
      }}
    >
      <Handle type="target" position={Position.Top} style={{ opacity: 0, width: 8, height: 8 }} />
      <div style={{ display: 'flex', flexDirection: 'column', alignItems: 'center' }}>
        <div style={{
          fontSize: 14,
          fontWeight: 600,
          color: color.text,
          lineHeight: 1.2,
        }}>
          {data.label}
        </div>
        <div style={{
          fontSize: 11,
          color: '#888',
          marginTop: 2,
          fontWeight: 500,
        }}>
          {data.count} · click to expand
        </div>
      </div>
      <Handle type="source" position={Position.Bottom} style={{ opacity: 0, width: 8, height: 8 }} />
    </div>
  );
};

// ── Expand header node — shown above expanded group, click to re-collapse ──
const GroupHeaderNode = ({ data }) => {
  const color = getColor(data.category);

  return (
    <div
      style={{
        width: NODE_W,
        height: 32,
        background: color.bg,
        border: `1px solid ${color.border}`,
        borderRadius: 6,
        display: 'flex',
        alignItems: 'center',
        justifyContent: 'center',
        cursor: 'pointer',
        padding: '2px 12px',
        position: 'relative',
        gap: 6,
      }}
    >
      <Handle type="target" position={Position.Top} style={{ opacity: 0, width: 8, height: 8 }} />
      <div style={{ fontSize: 12, fontWeight: 600, color: color.text }}>
        {data.label}
      </div>
      <div style={{ fontSize: 10, color: '#888' }}>
        click to collapse
      </div>
      <Handle type="source" position={Position.Bottom} style={{ opacity: 0, width: 8, height: 8 }} />
    </div>
  );
};

const nodeTypes = { process: ProcessNode, group: GroupNode, groupHeader: GroupHeaderNode };

// ── Dagre layout helper ──
const getLayoutedElements = (nodes, edges) => {
  const g = new dagre.graphlib.Graph();
  g.setDefaultEdgeLabel(() => ({}));
  g.setGraph({
    rankdir: 'TB',
    nodesep: 40,
    ranksep: 60,
    edgesep: 30,
    marginx: 30,
    marginy: 30,
  });

  nodes.forEach((node) => {
    const h = node.type === 'groupHeader' ? 32 : NODE_H;
    g.setNode(node.id, { width: NODE_W, height: h });
  });

  edges.forEach((edge) => {
    g.setEdge(edge.source, edge.target);
  });

  dagre.layout(g);

  const layoutedNodes = nodes.map((node) => {
    const pos = g.node(node.id);
    const h = node.type === 'groupHeader' ? 32 : NODE_H;
    return {
      ...node,
      position: { x: pos.x - NODE_W / 2, y: pos.y - h / 2 },
    };
  });

  return { nodes: layoutedNodes, edges };
};

// ── Identify groups of consecutive same-type processes ──
const identifyGroups = (processes) => {
  const procMap = {};
  processes.forEach(p => { procMap[p.id] = p; });

  const children = {};
  const parents = {};
  processes.forEach(p => { children[p.id] = []; parents[p.id] = []; });
  processes.forEach(p => {
    (p.next_process_ids || []).forEach(nid => {
      if (children[p.id]) children[p.id].push(nid);
      if (parents[nid]) parents[nid].push(p.id);
    });
  });

  const visited = new Set();
  const groups = [];

  const roots = processes.filter(p => parents[p.id].length === 0);

  const walkChain = (startId) => {
    const chain = [];
    let current = startId;
    while (current && !visited.has(current)) {
      const proc = procMap[current];
      if (!proc) break;
      chain.push(proc);
      visited.add(current);
      const kids = children[current] || [];
      if (kids.length === 1) {
        const kid = procMap[kids[0]];
        if (kid && (parents[kids[0]] || []).length === 1) {
          current = kids[0];
        } else {
          break;
        }
      } else {
        break;
      }
    }
    return chain;
  };

  const processChains = (startIds) => {
    for (const sid of startIds) {
      if (visited.has(sid)) continue;
      const chain = walkChain(sid);

      let runStart = 0;
      for (let i = 1; i <= chain.length; i++) {
        const sameType = i < chain.length &&
          (chain[i].process_type || '').toLowerCase() === (chain[runStart].process_type || '').toLowerCase();
        if (!sameType) {
          const run = chain.slice(runStart, i);
          if (run.length >= 3) {
            groups.push({
              type: run[0].process_type,
              processIds: run.map(p => p.id),
              firstId: run[0].id,
              lastId: run[run.length - 1].id,
            });
          }
          runStart = i;
        }
      }

      const lastProc = chain[chain.length - 1];
      if (lastProc) {
        const kids = children[lastProc.id] || [];
        processChains(kids);
      }
    }
  };

  processChains(roots.map(r => r.id));
  return groups;
};

const formatName = (name) => (name || '').replace(/_/g, ' ');
const formatCategory = (cat) => {
  if (!cat) return '';
  return cat.replace(/_/g, ' ').replace(/\b\w/g, (l) => l.toUpperCase());
};

// ── Build graph with collapsible groups ──
// When expanded, group processes are laid out horizontally (same dagre rank)
const buildGraph = (project, selectedProcess, collapsedGroups, groups) => {
  if (!project || !project.processes) return { nodes: [], edges: [] };

  const processes = Object.values(project.processes);
  const selectedId = selectedProcess?.id;
  const procMap = {};
  processes.forEach(p => { procMap[p.id] = p; });

  const uniqueCategories = new Set(processes.map(p => (p.manufacturing_category || '').toLowerCase()));
  const useProcessType = uniqueCategories.size <= 1;

  // Build collapsed/expanded mappings
  const collapsedProcIds = new Set();
  const expandedGroupIds = new Set();
  const procToGroup = {};
  const activeCollapsedGroups = [];
  const activeExpandedGroups = [];

  groups.forEach((grp, idx) => {
    const groupId = `group_${idx}`;
    if (collapsedGroups.has(groupId)) {
      grp.processIds.forEach(pid => {
        collapsedProcIds.add(pid);
        procToGroup[pid] = groupId;
      });
      activeCollapsedGroups.push({ ...grp, groupId });
    } else {
      expandedGroupIds.add(groupId);
      activeExpandedGroups.push({ ...grp, groupId });
    }
  });

  // Track which processes belong to an expanded group
  const expandedProcToGroup = {};
  activeExpandedGroups.forEach(grp => {
    grp.processIds.forEach(pid => { expandedProcToGroup[pid] = grp; });
  });

  const nodes = [];
  const edges = [];
  const addedGroups = new Set();

  // Add group header nodes for expanded groups
  activeExpandedGroups.forEach(grp => {
    const colorKey = useProcessType ? (grp.type || 'default') : 'default';
    nodes.push({
      id: grp.groupId,
      type: 'groupHeader',
      position: { x: 0, y: 0 },
      data: {
        label: formatCategory(grp.type),
        category: colorKey,
        groupId: grp.groupId,
      },
    });
  });

  processes.forEach((proc) => {
    // Skip collapsed processes, add group node instead
    if (collapsedProcIds.has(proc.id)) {
      const groupId = procToGroup[proc.id];
      if (!addedGroups.has(groupId)) {
        addedGroups.add(groupId);
        const grp = activeCollapsedGroups.find(g => g.groupId === groupId);
        const colorKey = useProcessType ? (grp.type || 'default') : 'default';
        nodes.push({
          id: groupId,
          type: 'group',
          position: { x: 0, y: 0 },
          data: {
            label: formatCategory(grp.type),
            count: `${grp.processIds.length} processes`,
            category: colorKey,
            groupId,
          },
        });
      }
      return;
    }

    const colorKey = useProcessType
      ? (proc.process_type || 'default')
      : (proc.manufacturing_category || proc.process_type || 'default');
    const labelKey = useProcessType ? proc.process_type : proc.manufacturing_category;

    nodes.push({
      id: proc.id,
      type: 'process',
      position: { x: 0, y: 0 },
      data: {
        label: formatName(proc.name),
        processId: proc.id,
        category: colorKey,
        categoryLabel: formatCategory(labelKey),
        isSelected: proc.id === selectedId,
        process: proc,
      },
    });
  });

  const nodeIds = new Set(nodes.map(n => n.id));

  // Add edges from group headers to their child processes (horizontal siblings)
  activeExpandedGroups.forEach(grp => {
    grp.processIds.forEach(pid => {
      if (nodeIds.has(pid)) {
        edges.push({
          id: `${grp.groupId}->${pid}`,
          source: grp.groupId,
          target: pid,
          type: 'smoothstep',
          animated: false,
          style: { stroke: getColor(useProcessType ? grp.type : 'default').border, strokeWidth: 1.5 },
          markerEnd: {
            type: MarkerType.ArrowClosed,
            width: 12,
            height: 12,
            color: getColor(useProcessType ? grp.type : 'default').border,
          },
        });
      }
    });
  });

  // Add normal process edges, remapping collapsed ones
  processes.forEach((proc) => {
    (proc.next_process_ids || []).forEach((nextId) => {
      let sourceId = collapsedProcIds.has(proc.id) ? procToGroup[proc.id] : proc.id;
      let targetId = collapsedProcIds.has(nextId) ? procToGroup[nextId] : nextId;

      // If source is in an expanded group, skip internal edges (handled by header)
      const srcGrp = expandedProcToGroup[proc.id];
      const tgtGrp = expandedProcToGroup[nextId];
      if (srcGrp && tgtGrp && srcGrp.groupId === tgtGrp.groupId) return;

      // If target is first process of expanded group, connect to header instead
      const tgtExpGrp = expandedProcToGroup[nextId];
      if (tgtExpGrp && nextId === tgtExpGrp.firstId) {
        targetId = tgtExpGrp.groupId;
      }

      // If source is last process of expanded group, connect from that process
      // (already handled since sourceId = proc.id)

      if (sourceId === targetId) return;
      if (!nodeIds.has(sourceId) || !nodeIds.has(targetId)) return;

      const edgeId = `${sourceId}->${targetId}`;
      if (edges.some(e => e.id === edgeId)) return;

      edges.push({
        id: edgeId,
        source: sourceId,
        target: targetId,
        type: 'smoothstep',
        animated: false,
        style: { stroke: '#999', strokeWidth: 1.5 },
        markerEnd: {
          type: MarkerType.ArrowClosed,
          width: 16,
          height: 16,
          color: '#999',
        },
      });
    });
  });

  // Connect last process of expanded group to the next process outside
  activeExpandedGroups.forEach(grp => {
    const lastProc = procMap[grp.lastId];
    if (!lastProc) return;
    (lastProc.next_process_ids || []).forEach(nextId => {
      let targetId = collapsedProcIds.has(nextId) ? procToGroup[nextId] : nextId;
      const tgtExpGrp = expandedProcToGroup[nextId];
      if (tgtExpGrp && nextId === tgtExpGrp.firstId) {
        targetId = tgtExpGrp.groupId;
      }
      if (!nodeIds.has(grp.lastId) || !nodeIds.has(targetId)) return;
      if (grp.lastId === targetId) return;

      // Add edges from ALL expanded processes to the next node (they fan back in)
      grp.processIds.forEach(pid => {
        if (!nodeIds.has(pid)) return;
        const edgeId = `${pid}->${targetId}`;
        if (edges.some(e => e.id === edgeId)) return;
        edges.push({
          id: edgeId,
          source: pid,
          target: targetId,
          type: 'smoothstep',
          animated: false,
          style: { stroke: '#999', strokeWidth: 1.5 },
          markerEnd: {
            type: MarkerType.ArrowClosed,
            width: 16,
            height: 16,
            color: '#999',
          },
        });
      });
    });
  });

  return getLayoutedElements(nodes, edges);
};

// ── Inner component ──
const DiagramInner = ({ project, selectedProcess, onSelectProcess }) => {
  const { fitView } = useReactFlow();

  const groups = useMemo(() => {
    if (!project || !project.processes) return [];
    return identifyGroups(Object.values(project.processes));
  }, [project]);

  // Start with all groups collapsed
  const [collapsedGroups, setCollapsedGroups] = useState(() => {
    const initial = new Set();
    groups.forEach((_, idx) => initial.add(`group_${idx}`));
    return initial;
  });

  useEffect(() => {
    const initial = new Set();
    groups.forEach((_, idx) => initial.add(`group_${idx}`));
    setCollapsedGroups(initial);
  }, [groups]);

  const { nodes: initialNodes, edges: initialEdges } = useMemo(
    () => buildGraph(project, selectedProcess, collapsedGroups, groups),
    [project, selectedProcess, collapsedGroups, groups]
  );

  const [nodes, setNodes, onNodesChange] = useNodesState(initialNodes);
  const [edges, setEdges, onEdgesChange] = useEdgesState(initialEdges);

  useEffect(() => {
    const { nodes: newNodes, edges: newEdges } = buildGraph(project, selectedProcess, collapsedGroups, groups);
    setNodes(newNodes);
    setEdges(newEdges);
    setTimeout(() => fitView({ padding: 0.15, duration: 200 }), 50);
  }, [project, selectedProcess, collapsedGroups, groups]);

  const onNodeClick = useCallback(
    (event, node) => {
      if (node.type === 'group') {
        // Expand
        setCollapsedGroups(prev => {
          const next = new Set(prev);
          next.delete(node.data.groupId);
          return next;
        });
      } else if (node.type === 'groupHeader') {
        // Re-collapse
        setCollapsedGroups(prev => {
          const next = new Set(prev);
          next.add(node.data.groupId);
          return next;
        });
      } else if (node.data.process && onSelectProcess) {
        onSelectProcess(node.data.process);
      }
    },
    [onSelectProcess]
  );

  return (
    <ReactFlow
      nodes={nodes}
      edges={edges}
      onNodesChange={onNodesChange}
      onEdgesChange={onEdgesChange}
      onNodeClick={onNodeClick}
      onPaneClick={() => { if (onSelectProcess) onSelectProcess(null); }}
      nodeTypes={nodeTypes}
      fitView
      fitViewOptions={{ padding: 0.15 }}
      minZoom={0.3}
      maxZoom={2}
      attributionPosition="bottom-left"
      proOptions={{ hideAttribution: true }}
      style={{ background: '#fafafa' }}
    >
      <Background color="#e0e0e0" gap={20} size={1} />
      <Controls
        showInteractive={false}
        style={{ bottom: 10, left: 10 }}
      />
    </ReactFlow>
  );
};

// ── Outer wrapper ──
const ProcessDiagram = ({ project, selectedProcess, onSelectProcess }) => {
  if (!project || !project.processes) {
    return (
      <div style={{
        width: '100%', height: '100%', display: 'flex',
        alignItems: 'center', justifyContent: 'center',
        color: '#999', fontSize: 14,
      }}>
        No process data available
      </div>
    );
  }

  return (
    <div style={{ width: '100%', height: '100%' }}>
      <ReactFlowProvider>
        <DiagramInner
          project={project}
          selectedProcess={selectedProcess}
          onSelectProcess={onSelectProcess}
        />
      </ReactFlowProvider>
    </div>
  );
};

export default ProcessDiagram;
